import React, { useCallback, useEffect, useState } from "react";

import { Loader2, Play } from "lucide-react";

import { formatDateTime } from "./format";
import {
  type PipelineHealth,
  type PipelineSettings,
  type PipelineStatus,
  type SignalAdminDraft,
} from "./types";
import Button from "../../Components/Button";
import { useAppDispatch } from "../../Store/Hooks";
import {
  fetchSignalPipelineHealth,
  fetchSignalPipelineStatus,
  runSignalSweep,
} from "../../Store/Reducers/SignalAdminSlice";

interface PipelinePanelProps {
  draft: SignalAdminDraft;
  /** The applied config's pipeline settings, to mark staged values. */
  appliedPipeline: PipelineSettings;
  onDraftChange: (next: SignalAdminDraft) => void;
}

const FIELDS: {
  key: keyof PipelineSettings;
  label: string;
  unit: string;
  hint: string;
  defaultKey: "server_default_sweep_minutes" | "server_default_recheck_hours";
}[] = [
  {
    key: "sweep_interval_minutes",
    label: "Signal sweep",
    unit: "minutes",
    hint: "How often queued checks run. Cards change at most this long after the data does.",
    defaultKey: "server_default_sweep_minutes",
  },
  {
    key: "recheck_interval_hours",
    label: "Full re-check",
    unit: "hours",
    hint: "How often every account is re-checked, so time-based signals (renewal countdowns, AL staleness) move without a data change.",
    defaultKey: "server_default_recheck_hours",
  },
];

const HEALTH_CLASS: Record<PipelineHealth["status"], string> = {
  ok: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  warning: "bg-amber-50 text-amber-800 ring-amber-600/20",
  alert: "bg-red-50 text-red-700 ring-red-600/20",
};

const when = (value: string | null | undefined) =>
  value ? formatDateTime(value) : "—";

const PipelinePanel: React.FC<PipelinePanelProps> = ({
  draft,
  appliedPipeline,
  onDraftChange,
}) => {
  const dispatch = useAppDispatch();
  const [status, setStatus] = useState<PipelineStatus | null>(null);
  const [health, setHealth] = useState<
    { org_id: string; org_name: string; health: PipelineHealth }[]
  >([]);
  const [error, setError] = useState("");
  const [runMessage, setRunMessage] = useState("");
  const [starting, setStarting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, h] = await Promise.all([
        dispatch(fetchSignalPipelineStatus()).unwrap(),
        dispatch(fetchSignalPipelineHealth()).unwrap(),
      ]);
      setStatus(s as PipelineStatus);
      setHealth(h?.organizations ?? []);
      setError("");
    } catch (e) {
      setError(typeof e === "string" ? e : "Couldn't load the sweeper.");
    }
  }, [dispatch]);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), 15000);
    return () => window.clearInterval(timer);
  }, [load]);

  const stage = (key: keyof PipelineSettings, raw: string) => {
    const next = { ...(draft.pipeline ?? {}) };
    const value = Number(raw);
    if (raw.trim() === "" || !Number.isFinite(value)) delete next[key];
    else next[key] = Math.round(value);
    onDraftChange({ ...draft, pipeline: next });
  };

  const runNow = async () => {
    setStarting(true);
    setRunMessage("");
    try {
      const res = await dispatch(runSignalSweep()).unwrap();
      setRunMessage(
        res?.started
          ? "Sweep started. This page refreshes as it runs."
          : "A sweep is already running."
      );
      window.setTimeout(() => void load(), 2000);
      window.setTimeout(() => void load(), 8000);
    } catch (e) {
      setRunMessage(typeof e === "string" ? e : "Couldn't start a sweep.");
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <div>
        <span className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-brand-dark">
          Signal pipeline
        </span>
        <h3 className="text-[15px] font-bold text-gray-900">Sweeper</h3>
        <p className="mt-0.5 max-w-3xl text-[12.5px] text-gray-500">
          How often signals are re-checked, for every organization. Changes go
          out with Review and apply and take effect within a minute, with no
          restart.
        </p>
      </div>

      {error && (
        <p className="m-0 rounded-md bg-red-50 px-3 py-2 text-[12.5px] text-red-700">
          {error}
        </p>
      )}

      <section className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
          <div className="flex items-center justify-between gap-2">
            <h4 className="m-0 text-[13px] font-bold text-gray-900">
              Signal sweep
            </h4>
            {status?.sweep_in_progress && (
              <span className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-brand">
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                Running now
              </span>
            )}
          </div>
          <dl className="m-0 mt-2 grid grid-cols-[110px_minmax(0,1fr)] gap-y-1 text-[12.5px]">
            <dt className="text-gray-500">Every</dt>
            <dd className="m-0 text-gray-900">
              {status ? `${status.sweep_interval_minutes} min` : "—"}
              {status && (
                <span className="text-gray-400">
                  {" "}
                  ·{" "}
                  {status.sweep_source === "admin"
                    ? "set here"
                    : "server default"}
                </span>
              )}
            </dd>
            <dt className="text-gray-500">Last sweep</dt>
            <dd className="m-0 text-gray-900">
              {when(status?.last_sweep_ended)}
              {status?.last_sweep_trigger === "manual" && (
                <span className="text-gray-400"> · run manually</span>
              )}
              {status?.last_sweep_totals && (
                <div className="text-[11.5px] text-gray-500">
                  {status.last_sweep_totals.claimed ?? 0} checks ·{" "}
                  {status.last_sweep_totals.failed ?? 0} failed
                </div>
              )}
            </dd>
            <dt className="text-gray-500">Next sweep</dt>
            <dd className="m-0 text-gray-900">
              {status?.sweeper_running
                ? when(status.next_sweep_due)
                : "Sweeper not running in this API process"}
            </dd>
          </dl>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button
              variant="primary"
              onClick={() => void runNow()}
              disabled={starting || Boolean(status?.sweep_in_progress)}
            >
              <span className="inline-flex items-center gap-1.5">
                <Play className="h-3.5 w-3.5" aria-hidden />
                Run sweep now
              </span>
            </Button>
            {runMessage && (
              <span className="text-[12px] text-gray-600">{runMessage}</span>
            )}
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
          <h4 className="m-0 text-[13px] font-bold text-gray-900">
            Full re-check
          </h4>
          <dl className="m-0 mt-2 grid grid-cols-[110px_minmax(0,1fr)] gap-y-1 text-[12.5px]">
            <dt className="text-gray-500">Every</dt>
            <dd className="m-0 text-gray-900">
              {status ? `${status.recheck_interval_hours} h` : "—"}
              {status && (
                <span className="text-gray-400">
                  {" "}
                  ·{" "}
                  {status.recheck_source === "admin"
                    ? "set here"
                    : "server default"}
                </span>
              )}
            </dd>
            <dt className="text-gray-500">Last run</dt>
            <dd className="m-0 text-gray-900">{when(status?.last_recheck)}</dd>
            <dt className="text-gray-500">Next run</dt>
            <dd className="m-0 text-gray-900">
              {status?.recheck_running
                ? when(status.next_recheck_due)
                : "Not running in this API process"}
            </dd>
          </dl>
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white px-4 py-3">
        <h4 className="m-0 text-[13px] font-bold text-gray-900">Intervals</h4>
        <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2">
          {FIELDS.map(f => {
            const [low, high] = status?.bounds?.[f.key] ?? [1, 60];
            const value = draft.pipeline?.[f.key];
            const staged =
              (value ?? null) !== (appliedPipeline?.[f.key] ?? null);
            const invalid =
              value !== undefined && (value < low || value > high);
            return (
              <label key={f.key} className="flex flex-col gap-1">
                <span className="text-[11px] font-bold uppercase tracking-[0.06em] text-gray-500">
                  {f.label} · {f.unit}
                  {staged && (
                    <span className="ml-2 normal-case tracking-normal text-amber-700">
                      Unapplied
                    </span>
                  )}
                </span>
                <input
                  type="number"
                  min={low}
                  max={high}
                  value={value ?? ""}
                  placeholder={
                    status
                      ? `Server default: ${status[f.defaultKey]}`
                      : "Server default"
                  }
                  onChange={e => stage(f.key, e.target.value)}
                  className={`w-40 rounded-md border px-2.5 py-1.5 text-[12.5px] focus:outline-none focus:ring-1 ${
                    invalid
                      ? "border-red-400 focus:ring-red-400"
                      : "border-gray-300 focus:border-brand focus:ring-brand"
                  }`}
                />
                <span className="text-[11.5px] text-gray-500">
                  {f.hint} Allowed {low}–{high}; leave empty for the server
                  default.
                </span>
                {invalid && (
                  <span className="text-[11.5px] text-red-600">
                    Must be between {low} and {high}.
                  </span>
                )}
              </label>
            );
          })}
        </div>
      </section>

      <section className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <div className="border-b border-gray-100 px-4 py-2.5">
          <h4 className="m-0 text-[13px] font-bold text-gray-900">
            Pipeline health by organization
          </h4>
        </div>
        <table className="w-full border-collapse text-left text-[12.5px]">
          <thead>
            <tr className="bg-gray-50 text-[10px] font-bold uppercase tracking-[0.07em] text-gray-400">
              <th className="px-4 py-2">Organization</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Details</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {health.map(o => (
              <tr key={o.org_id} className="align-top">
                <td className="px-4 py-2.5 font-semibold text-gray-900">
                  {o.org_name}
                </td>
                <td className="px-3 py-2.5">
                  <span
                    className={`inline-flex rounded-full px-2 py-0.5 text-[10.5px] font-bold capitalize ring-1 ring-inset ${HEALTH_CLASS[o.health.status]}`}
                  >
                    {o.health.status}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-gray-600">
                  {o.health.reasons.length
                    ? o.health.reasons.join(" ")
                    : "No problems."}
                </td>
              </tr>
            ))}
            {health.length === 0 && (
              <tr>
                <td colSpan={3} className="px-4 py-4 text-gray-400">
                  Loading…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
};

export default PipelinePanel;
